import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import EditorCanvas from './editor/EditorCanvas';
import { EditorToolbar, EditorLeftPanel, EditorRightPanel, ExportDialog } from './editor/EditorPanels';
import {
  TOOLS, DEFAULT_STYLE, KEYBOARD_SHORTCUTS, GRID_SIZE,
  genId, getBBox, snapToGrid, getSnapGuides, alignObjects, distributeObjects,
  pointsToPath, BIO_TEMPLATES,
  exportAsSVG, exportAsPNG, exportAsPDF,
  translatePathD, scalePathD
} from './editor/editorHelpers';
import {
  booleanUnite, booleanSubtract, booleanIntersect, booleanExclude,
  createLinearGradient, createRadialGradient,
  createPattern, createBlend,
  selectBySimilarity, selectByLasso,
  createSymbol, instantiateSymbol,
  createClipMask,
  parseSVGPath, pointsToSVGPath, convertAnchorSmooth,
  shapeToAnchorPoints, anchorPointsToPath, toggleAnchorPointSmooth, dragAnchorHandle,
  catmullRomToBezier,
} from './editor/advancedFeatures';

const FigureMakerPage = () => {
  // State
  const [objects, setObjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tool, setTool] = useState(TOOLS.SELECT);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [gridEnabled, setGridEnabled] = useState(true);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [showRulers, setShowRulers] = useState(true);
  const [currentStyle, setCurrentStyle] = useState({ ...DEFAULT_STYLE });
  const [figureName, setFigureName] = useState('Untitled Figure');
  const [figures, setFigures] = useState([]);
  const [loading, setLoading] = useState(true);

  // History
  const [history, setHistory] = useState([[]]);
  const [historyIdx, setHistoryIdx] = useState(0);

  // Pen tool
  const [penPoints, setPenPoints] = useState([]);

  // Interaction
  const [dragState, setDragState] = useState(null);
  const [snapGuides, setSnapGuides] = useState([]);
  const [rubberBand, setRubberBand] = useState(null);

  // Lasso
  const [lassoPoints, setLassoPoints] = useState([]);

  // Direct select
  const [directSelectPoints, setDirectSelectPoints] = useState([]);
  const [directSelectIdx, setDirectSelectIdx] = useState(-1);

  // Symbols
  const [symbols, setSymbols] = useState([]);

  // Clip masks
  const [clipMasks, setClipMasks] = useState([]);

  // Anchor Point tool
  const [anchorPoints, setAnchorPoints] = useState([]);
  const [anchorSelectedIdx, setAnchorSelectedIdx] = useState(-1);
  const [anchorDragging, setAnchorDragging] = useState(null);
  const [anchorObjId, setAnchorObjId] = useState(null);

  // Context menu
  const [contextMenu, setContextMenu] = useState(null);

  // AI
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Export
  const [showExport, setShowExport] = useState(false);

  // Canvas size
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 900, h: 600 });

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ w: rect.width, h: rect.height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => { loadFigures(); }, []);

  const loadFigures = async () => {
    try { const data = await api.getFigures(); setFigures(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ==================== HISTORY ====================
  const pushHistory = useCallback((newObjects) => {
    const newHistory = history.slice(0, historyIdx + 1);
    newHistory.push(JSON.parse(JSON.stringify(newObjects)));
    if (newHistory.length > 100) newHistory.shift();
    setHistory(newHistory);
    setHistoryIdx(newHistory.length - 1);
  }, [history, historyIdx]);

  const undo = useCallback(() => {
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      setObjects(JSON.parse(JSON.stringify(history[newIdx])));
      setSelectedIds([]);
    }
  }, [historyIdx, history]);

  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      setObjects(JSON.parse(JSON.stringify(history[newIdx])));
    }
  }, [historyIdx, history]);

  // ==================== OBJECT CRUD ====================
  const updateObject = useCallback((id, updates) => {
    setObjects(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
  }, []);

  const updateObjectAndCommit = useCallback((id, updates) => {
    const newObjects = objects.map(o => o.id === id ? { ...o, ...updates } : o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [objects, pushHistory]);

  const addObject = useCallback((obj) => {
    const newObj = { id: genId(), visible: true, locked: false, opacity: 1, ...currentStyle, ...obj };
    const newObjects = [...objects, newObj];
    setObjects(newObjects);
    pushHistory(newObjects);
    setSelectedIds([newObj.id]);
    setTool(TOOLS.SELECT);
    return newObj;
  }, [objects, currentStyle, pushHistory]);

  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return;
    const newObjects = objects.filter(o => !selectedIds.includes(o.id));
    setObjects(newObjects);
    pushHistory(newObjects);
    setSelectedIds([]);
  }, [selectedIds, objects, pushHistory]);

  const duplicateSelected = useCallback(() => {
    const newObjs = [...objects];
    const newIds = [];
    selectedIds.forEach(id => {
      const obj = objects.find(o => o.id === id);
      if (!obj) return;
      const clone = { ...JSON.parse(JSON.stringify(obj)), id: genId() };
      if (clone.x !== undefined) { clone.x += 20; clone.y += 20; }
      if (clone.x1 !== undefined) { clone.x1 += 20; clone.y1 += 20; clone.x2 += 20; clone.y2 += 20; }
      newObjs.push(clone);
      newIds.push(clone.id);
    });
    setObjects(newObjs);
    pushHistory(newObjs);
    setSelectedIds(newIds);
  }, [selectedIds, objects, pushHistory]);

  // ==================== ALIGNMENT ====================
  const handleAlign = useCallback((alignment) => {
    const selected = objects.filter(o => selectedIds.includes(o.id));
    const aligned = alignObjects(selected, alignment);
    const idMap = {};
    aligned.forEach(o => { idMap[o.id] = o; });
    const newObjects = objects.map(o => idMap[o.id] || o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [selectedIds, objects, pushHistory]);

  const handleDistribute = useCallback((direction) => {
    const selected = objects.filter(o => selectedIds.includes(o.id));
    const distributed = distributeObjects(selected, direction);
    const idMap = {};
    distributed.forEach(o => { idMap[o.id] = o; });
    const newObjects = objects.map(o => idMap[o.id] || o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [selectedIds, objects, pushHistory]);

  // ==================== LAYER MANAGEMENT ====================
  const moveLayer = useCallback((id, dir) => {
    const idx = objects.findIndex(o => o.id === id);
    if (idx < 0) return;
    const swapIdx = dir === 'up' ? idx + 1 : idx - 1;
    if (swapIdx < 0 || swapIdx >= objects.length) return;
    const newObjects = [...objects];
    [newObjects[idx], newObjects[swapIdx]] = [newObjects[swapIdx], newObjects[idx]];
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [objects, pushHistory]);

  const toggleVisibility = useCallback((id) => {
    updateObject(id, { visible: objects.find(o => o.id === id)?.visible === false ? true : false });
  }, [objects, updateObject]);

  const toggleLock = useCallback((id) => {
    updateObject(id, { locked: !objects.find(o => o.id === id)?.locked });
  }, [objects, updateObject]);

  // ==================== SVG COORDINATE HELPERS ====================
  const svgPoint = useCallback((e) => {
    return {
      x: (e.clientX - (containerRef.current?.getBoundingClientRect().left || 0) - panX) / zoom,
      y: (e.clientY - (containerRef.current?.getBoundingClientRect().top || 0) - panY) / zoom,
    };
  }, [zoom, panX, panY]);

  const maybeSnap = useCallback((x, y) => {
    if (!gridEnabled || !snapEnabled) return { x, y };
    return { x: snapToGrid(x), y: snapToGrid(y) };
  }, [gridEnabled, snapEnabled]);

  // ==================== MOUSE HANDLERS ====================
  const handleCanvasClick = useCallback((e) => {
    const pt = svgPoint(e);
    const snapped = maybeSnap(pt.x, pt.y);

    if (tool === TOOLS.PAN) {
      setDragState({ type: 'pan', startX: e.clientX, startY: e.clientY, startPanX: panX, startPanY: panY });
      return;
    }

    if (tool === TOOLS.SELECT) {
      // Start rubber band selection
      setSelectedIds([]);
      setDragState({ type: 'rubberband', startX: pt.x, startY: pt.y });
      return;
    }

    if (tool === TOOLS.PEN) {
      // Add point to pen path. If user drags, bezier handles are created in handleMouseMove
      const newPoint = { x: snapped.x, y: snapped.y };
      setPenPoints(prev => [...prev, newPoint]);
      // Start drag to create bezier handles
      setDragState({ type: 'pen-handle', pointIndex: penPoints.length, startX: snapped.x, startY: snapped.y });
      return;
    }

    if (tool === TOOLS.CURVATURE) {
      // Curvature pen: auto-smooth curves using Catmull-Rom spline interpolation
      const newPoint = { x: snapped.x, y: snapped.y };
      setPenPoints(prev => {
        const rawPoints = [...prev, newPoint];
        // Apply Catmull-Rom spline to generate smooth bezier handles
        if (rawPoints.length >= 3) {
          return catmullRomToBezier(rawPoints, 0.3, false);
        }
        return rawPoints;
      });
      return;
    }

    if (tool === TOOLS.LASSO) {
      // Start lasso selection
      setLassoPoints([{ x: pt.x, y: pt.y }]);
      setDragState({ type: 'lasso' });
      return;
    }

    if (tool === TOOLS.MAGIC_WAND) {
      // Find object at click point and select similar
      const clickedObj = [...objects].reverse().find(o => {
        if (o.visible === false) return false;
        const bbox = getBBox(o);
        return pt.x >= bbox.x && pt.x <= bbox.x + bbox.w && pt.y >= bbox.y && pt.y <= bbox.y + bbox.h;
      });
      if (clickedObj) {
        const similarIds = selectBySimilarity(objects, clickedObj, 80);
        setSelectedIds(similarIds);
        toast.success(`Selected ${similarIds.length} similar objects`);
      }
      return;
    }

    if (tool === TOOLS.BLEND) {
      // Blend: click on two objects in sequence
      const clickedObj = [...objects].reverse().find(o => {
        if (o.visible === false) return false;
        const bbox = getBBox(o);
        return pt.x >= bbox.x && pt.x <= bbox.x + bbox.w && pt.y >= bbox.y && pt.y <= bbox.y + bbox.h;
      });
      if (clickedObj) {
        if (selectedIds.length === 0) {
          setSelectedIds([clickedObj.id]);
          toast.info('Select second object for blend');
        } else if (selectedIds.length === 1 && selectedIds[0] !== clickedObj.id) {
          const objA = objects.find(o => o.id === selectedIds[0]);
          if (objA) {
            const blended = createBlend(objA, clickedObj, 5);
            const all = [...objects, ...blended];
            setObjects(all);
            pushHistory(all);
            setSelectedIds([]);
            setTool(TOOLS.SELECT);
            toast.success(`Created blend with ${blended.length} steps`);
          }
        }
      }
      return;
    }

    if (tool === TOOLS.DIRECT_SELECT) {
      // Click on canvas deselects
      setSelectedIds([]);
      setDirectSelectPoints([]);
      setDirectSelectIdx(-1);
      return;
    }

    if (tool === TOOLS.ANCHOR_POINT) {
      // If clicking on canvas without an object selected, try to select one
      const clickedObj = [...objects].reverse().find(o => {
        if (o.visible === false) return false;
        const bbox = getBBox(o);
        return pt.x >= bbox.x && pt.x <= bbox.x + bbox.w && pt.y >= bbox.y && pt.y <= bbox.y + bbox.h;
      });
      if (clickedObj) {
        setSelectedIds([clickedObj.id]);
      }
      return;
    }

    if (tool === TOOLS.TEXT) {
      addObject({ type: 'text', x: snapped.x, y: snapped.y, w: 150, h: 30, text: 'Text', fill: currentStyle.fill || '#0B1D3A', stroke: 'none', strokeWidth: 0 });
      return;
    }

    if (tool === TOOLS.ARTBOARD) {
      const labels = objects.filter(o => o.type === 'artboard').length;
      addObject({ type: 'artboard', x: snapped.x, y: snapped.y, w: 350, h: 250, label: String.fromCharCode(65 + labels), fill: '#FFFFFF', stroke: '#CBD5E1', strokeWidth: 1 });
      return;
    }

    // Shape/line/arrow/connector tools - start drag to create
    if ([TOOLS.RECT, TOOLS.ELLIPSE, TOOLS.LINE, TOOLS.POLYGON, TOOLS.ARROW,
      TOOLS.CONNECTOR_STRAIGHT, TOOLS.CONNECTOR_CURVED, TOOLS.CONNECTOR_ELBOW].includes(tool)) {
      setDragState({ type: 'create', tool, startX: snapped.x, startY: snapped.y });
    }
  }, [tool, svgPoint, maybeSnap, panX, panY, addObject, currentStyle, objects]);

  const handleObjectMouseDown = useCallback((e, objId) => {
    if (tool !== TOOLS.SELECT && tool !== TOOLS.DIRECT_SELECT && tool !== TOOLS.ANCHOR_POINT) return;
    e.stopPropagation();
    const obj = objects.find(o => o.id === objId);
    if (obj?.locked) return;

    const pt = svgPoint(e);
    const isShift = e.shiftKey;

    if (tool === TOOLS.ANCHOR_POINT) {
      setSelectedIds([objId]);
      return;
    }

    if (tool === TOOLS.DIRECT_SELECT) {
      setSelectedIds([objId]);
      // Direct select will show anchor points via the useEffect above
      return;
    }

    if (isShift) {
      setSelectedIds(prev => prev.includes(objId) ? prev.filter(id => id !== objId) : [...prev, objId]);
    } else {
      if (!selectedIds.includes(objId)) setSelectedIds([objId]);
    }

    setDragState({ type: 'move', startX: pt.x, startY: pt.y, origObjects: JSON.parse(JSON.stringify(objects)), objIds: isShift ? [...selectedIds, objId] : selectedIds.includes(objId) ? selectedIds : [objId] });
  }, [tool, objects, selectedIds, svgPoint]);

  const handleResizeStart = useCallback((e, type, handle, objId) => {
    e.stopPropagation();
    const pt = svgPoint(e);
    const obj = objects.find(o => o.id === objId);
    if (!obj) return;
    setDragState({ type, handle, objId, startX: pt.x, startY: pt.y, origObj: JSON.parse(JSON.stringify(obj)) });
  }, [objects, svgPoint]);

  const handleMouseMove = useCallback((e) => {
    if (!dragState) return;
    const pt = svgPoint(e);

    if (dragState.type === 'pan') {
      const dx = e.clientX - dragState.startX;
      const dy = e.clientY - dragState.startY;
      setPanX(dragState.startPanX + dx);
      setPanY(dragState.startPanY + dy);
      return;
    }

    if (dragState.type === 'rubberband') {
      const x = Math.min(dragState.startX, pt.x);
      const y = Math.min(dragState.startY, pt.y);
      const w = Math.abs(pt.x - dragState.startX);
      const h = Math.abs(pt.y - dragState.startY);
      setRubberBand({ x, y, w, h });
      // Select objects that intersect
      const intersecting = objects.filter(o => {
        const bbox = getBBox(o);
        return bbox.x < x + w && bbox.x + bbox.w > x && bbox.y < y + h && bbox.y + bbox.h > y;
      }).map(o => o.id);
      setSelectedIds(intersecting);
      return;
    }

    if (dragState.type === 'lasso') {
      setLassoPoints(prev => [...prev, { x: pt.x, y: pt.y }]);
      const ids = selectByLasso(objects, [...lassoPoints, { x: pt.x, y: pt.y }]);
      setSelectedIds(ids);
      return;
    }

    if (dragState.type === 'pen-handle') {
      // Drag to create bezier handles on the last pen point
      const idx = dragState.pointIndex;
      const dx = pt.x - dragState.startX;
      const dy = pt.y - dragState.startY;
      // Only add handles if the user has dragged a meaningful distance
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        setPenPoints(prev => {
          const updated = [...prev];
          if (updated[idx]) {
            updated[idx] = {
              ...updated[idx],
              cx1: updated[idx].x - dx,
              cy1: updated[idx].y - dy,
              cx2: updated[idx].x + dx,
              cy2: updated[idx].y + dy,
            };
          }
          return updated;
        });
      }
      return;
    }

    if (dragState.type === 'direct-point') {
      // Move anchor point
      if (directSelectPoints.length > 0 && selectedIds.length === 1) {
        const pts = [...directSelectPoints];
        const idx = dragState.handle;
        if (pts[idx]) {
          pts[idx] = { ...pts[idx], x: pt.x, y: pt.y };
          setDirectSelectPoints(pts);
          const d = pointsToSVGPath(pts);
          updateObject(selectedIds[0], { d });
        }
      }
      return;
    }

    if (dragState.type === 'direct-handle') {
      // Move control handle
      if (directSelectPoints.length > 0 && selectedIds.length === 1) {
        const [handleType, idxStr] = dragState.handle.split('-');
        const idx = Number(idxStr);
        const pts = [...directSelectPoints];
        if (pts[idx]) {
          if (handleType === 'cx1') pts[idx] = { ...pts[idx], cx1: pt.x, cy1: pt.y };
          else if (handleType === 'cx2') pts[idx] = { ...pts[idx], cx2: pt.x, cy2: pt.y };
          setDirectSelectPoints(pts);
          const d = pointsToSVGPath(pts);
          updateObject(selectedIds[0], { d });
        }
      }
      return;
    }

    if (dragState.type === 'move') {
      const dx = pt.x - dragState.startX;
      const dy = pt.y - dragState.startY;
      const snappedDx = snapEnabled && gridEnabled ? snapToGrid(dx, GRID_SIZE) : dx;
      const snappedDy = snapEnabled && gridEnabled ? snapToGrid(dy, GRID_SIZE) : dy;

      setObjects(dragState.origObjects.map(o => {
        if (!dragState.objIds.includes(o.id)) return o;
        if (o.type === 'path' && o.d) return { ...o, d: translatePathD(o.d, snappedDx, snappedDy) };
        if (o.type === 'group' && o.children) {
          const movedChildren = o.children.map(c =>
            c.type === 'path' && c.d ? { ...c, d: translatePathD(c.d, snappedDx, snappedDy) } :
            c.x !== undefined ? { ...c, x: c.x + snappedDx, y: c.y + snappedDy } :
            c.x1 !== undefined ? { ...c, x1: c.x1 + snappedDx, y1: c.y1 + snappedDy, x2: c.x2 + snappedDx, y2: c.y2 + snappedDy } : c
          );
          return { ...o, x: o.x + snappedDx, y: o.y + snappedDy, children: movedChildren };
        }
        if (o.x !== undefined) return { ...o, x: o.x + snappedDx, y: o.y + snappedDy };
        if (o.x1 !== undefined) return { ...o, x1: o.x1 + snappedDx, y1: o.y1 + snappedDy, x2: o.x2 + snappedDx, y2: o.y2 + snappedDy };
        return o;
      }));

      // Snap guides
      if (snapEnabled && dragState.objIds.length === 1) {
        const moved = objects.find(o => o.id === dragState.objIds[0]);
        if (moved) setSnapGuides(getSnapGuides(getBBox(moved), objects, dragState.objIds));
      }
      return;
    }

    if (dragState.type === 'resize') {
      const orig = dragState.origObj;
      const dx = pt.x - dragState.startX;
      const dy = pt.y - dragState.startY;
      const h = dragState.handle;

      // For paths, compute bbox from d and scale the d string
      if (orig.type === 'path' && orig.d) {
        const origBBox = getBBox(orig);
        let { x, y, w, h: height } = { x: origBBox.x, y: origBBox.y, w: origBBox.w, h: origBBox.h };
        if (h.includes('e')) w = Math.max(10, origBBox.w + dx);
        if (h.includes('w')) { w = Math.max(10, origBBox.w - dx); x = origBBox.x + dx; }
        if (h.includes('s')) height = Math.max(10, origBBox.h + dy);
        if (h.includes('n')) { height = Math.max(10, origBBox.h - dy); y = origBBox.y + dy; }
        const newD = scalePathD(orig.d, origBBox, { x, y, w, h: height });
        updateObject(dragState.objId, { d: newD });
        return;
      }

      let { x, y, w, h: height } = { x: orig.x, y: orig.y, w: orig.w, h: orig.h };

      if (h.includes('e')) w = Math.max(10, orig.w + dx);
      if (h.includes('w')) { w = Math.max(10, orig.w - dx); x = orig.x + dx; }
      if (h.includes('s')) height = Math.max(10, orig.h + dy);
      if (h.includes('n')) { height = Math.max(10, orig.h - dy); y = orig.y + dy; }

      updateObject(dragState.objId, { x, y, w, h: height });
      return;
    }

    if (dragState.type === 'rotate') {
      const orig = dragState.origObj;
      const bbox = getBBox(orig);
      const cx = bbox.x + bbox.w / 2;
      const cy = bbox.y + bbox.h / 2;
      const angle = Math.atan2(pt.y - cy, pt.x - cx) * (180 / Math.PI) + 90;
      updateObject(dragState.objId, { rotation: Math.round(((angle % 360) + 360) % 360) });
      return;
    }

    if (dragState.type === 'create') {
      const snapped = maybeSnap(pt.x, pt.y);
      const sx = dragState.startX;
      const sy = dragState.startY;
      const x = Math.min(sx, snapped.x);
      const y = Math.min(sy, snapped.y);
      const w = Math.abs(snapped.x - sx);
      const h = Math.abs(snapped.y - sy);

      if (!dragState.createdId) {
        let newObj;
        if (dragState.tool === TOOLS.RECT) {
          newObj = { type: 'rect', x, y, w, h };
        } else if (dragState.tool === TOOLS.ELLIPSE) {
          newObj = { type: 'ellipse', x, y, w, h };
        } else if (dragState.tool === TOOLS.POLYGON) {
          // Regular hexagon
          const cx2 = x + w / 2, cy2 = y + h / 2, r = Math.min(w, h) / 2;
          const pts = Array.from({ length: 6 }, (_, i) => {
            const a = (Math.PI / 3) * i - Math.PI / 2;
            return `${cx2 + r * Math.cos(a)},${cy2 + r * Math.sin(a)}`;
          }).join(' ');
          newObj = { type: 'polygon', x, y, w, h, points: pts };
        } else if ([TOOLS.LINE, TOOLS.ARROW, TOOLS.CONNECTOR_STRAIGHT, TOOLS.CONNECTOR_CURVED, TOOLS.CONNECTOR_ELBOW].includes(dragState.tool)) {
          const type = dragState.tool === TOOLS.LINE ? 'line' : dragState.tool === TOOLS.ARROW ? 'arrow' : dragState.tool;
          newObj = { type, x1: sx, y1: sy, x2: snapped.x, y2: snapped.y, label: '' };
        }
        if (newObj) {
          const obj = { id: genId(), visible: true, locked: false, opacity: 1, ...currentStyle, ...newObj };
          setObjects(prev => [...prev, obj]);
          setDragState({ ...dragState, createdId: obj.id });
          setSelectedIds([obj.id]);
        }
      } else {
        // Update the creating object
        if ([TOOLS.RECT, TOOLS.ELLIPSE, TOOLS.POLYGON].includes(dragState.tool)) {
          updateObject(dragState.createdId, { x, y, w, h });
          if (dragState.tool === TOOLS.POLYGON) {
            const cx2 = x + w / 2, cy2 = y + h / 2, r = Math.min(w, h) / 2;
            const pts = Array.from({ length: 6 }, (_, i) => {
              const a = (Math.PI / 3) * i - Math.PI / 2;
              return `${cx2 + r * Math.cos(a)},${cy2 + r * Math.sin(a)}`;
            }).join(' ');
            updateObject(dragState.createdId, { x, y, w, h, points: pts });
          }
        } else {
          updateObject(dragState.createdId, { x2: snapped.x, y2: snapped.y });
        }
      }
    }
  }, [dragState, svgPoint, snapEnabled, gridEnabled, objects, updateObject, currentStyle, maybeSnap]);

  const handleMouseUp = useCallback(() => {
    if (dragState) {
      if (dragState.type === 'move' || dragState.type === 'resize' || dragState.type === 'rotate' || dragState.type === 'create') {
        pushHistory([...objects]);
      }
      if (dragState.type === 'lasso') {
        setLassoPoints([]);
      }
      if (dragState.type === 'direct-point' || dragState.type === 'direct-handle') {
        pushHistory([...objects]);
      }
      // pen-handle drag ends silently — points are already updated
      setDragState(null);
      setSnapGuides([]);
      setRubberBand(null);
    }
  }, [dragState, objects, pushHistory]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.max(0.1, Math.min(8, prev + delta)));
    } else {
      setPanX(prev => prev - e.deltaX);
      setPanY(prev => prev - e.deltaY);
    }
  }, []);

  // ==================== KEYBOARD SHORTCUTS ====================
  useEffect(() => {
    const handler = (e) => {
      // Don't handle if in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (ctrl && e.key === 'y') { e.preventDefault(); redo(); return; }
      if (ctrl && e.key === 'c') { /* copy handled by browser */ return; }
      if (ctrl && e.key === 'd') { e.preventDefault(); duplicateSelected(); return; }
      if (ctrl && e.key === 'a') { e.preventDefault(); setSelectedIds(objects.map(o => o.id)); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') { deleteSelected(); return; }
      if (e.key === 'Escape') {
        if (penPoints.length > 0) {
          // Finish pen/curvature path
          if (penPoints.length >= 2) {
            const d = pointsToPath(penPoints);
            addObject({ type: 'path', d, x: 0, y: 0, w: 100, h: 100, fill: tool === TOOLS.CURVATURE ? currentStyle.fill : 'none' });
          }
          setPenPoints([]);
        } else {
          setSelectedIds([]);
          setTool(TOOLS.SELECT);
        }
        return;
      }
      if (e.key === 'Enter' && (tool === TOOLS.PEN || tool === TOOLS.CURVATURE) && penPoints.length >= 2) {
        const d = pointsToPath(penPoints);
        addObject({ type: 'path', d, x: 0, y: 0, w: 100, h: 100, fill: tool === TOOLS.CURVATURE ? currentStyle.fill : 'none' });
        setPenPoints([]);
        return;
      }

      // Tool shortcuts
      if (e.key === 'C' && e.shiftKey && !ctrl) { setTool(TOOLS.ANCHOR_POINT); return; }
      const t = KEYBOARD_SHORTCUTS[e.key.toLowerCase()];
      if (t && !ctrl) { setTool(t); return; }

      // Nudge with arrow keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedIds.length > 0) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        const newObjects = objects.map(o => {
          if (!selectedIds.includes(o.id)) return o;
          if (o.type === 'path' && o.d) return { ...o, d: translatePathD(o.d, dx, dy) };
          if (o.x !== undefined) return { ...o, x: o.x + dx, y: o.y + dy };
          if (o.x1 !== undefined) return { ...o, x1: o.x1 + dx, y1: o.y1 + dy, x2: o.x2 + dx, y2: o.y2 + dy };
          return o;
        });
        setObjects(newObjects);
      }

      // Zoom shortcuts
      if (ctrl && (e.key === '=' || e.key === '+')) { e.preventDefault(); setZoom(prev => Math.min(8, prev + 0.25)); }
      if (ctrl && e.key === '-') { e.preventDefault(); setZoom(prev => Math.max(0.1, prev - 0.25)); }
      if (ctrl && e.key === '0') { e.preventDefault(); setZoom(1); setPanX(0); setPanY(0); }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tool, selectedIds, objects, penPoints, undo, redo, deleteSelected, duplicateSelected, addObject]);

  // ==================== STYLE CHANGES ====================
  const handleStyleChange = useCallback((key, value) => {
    setCurrentStyle(prev => ({ ...prev, [key]: value }));
  }, []);

  // ==================== TEMPLATES ====================
  const loadTemplate = useCallback((name) => {
    const template = BIO_TEMPLATES[name];
    if (!template) return;
    const newObjects = template.map(o => ({
      id: genId(), visible: true, locked: false, opacity: 1,
      strokeWidth: 2, stroke: '#0B1D3A', fill: '#0D9488',
      ...o,
    }));
    setObjects(newObjects);
    pushHistory(newObjects);
    setFigureName(name);
    setSelectedIds([]);
  }, [pushHistory]);

  // ==================== SAVE / LOAD / EXPORT ====================
  const saveFigure = async () => {
    try {
      const svgContent = ''; // Generate on demand during export
      await api.createFigure({ name: figureName, figure_type: 'custom', data: { objects }, svg_content: svgContent, tags: [] });
      toast.success('Figure saved');
      loadFigures();
    } catch (e) { toast.error('Failed to save'); }
  };

  const loadSavedFigure = (fig) => {
    if (fig.data?.objects) {
      const loaded = fig.data.objects.map(o => ({ ...o, id: o.id || genId() }));
      setObjects(loaded);
      pushHistory(loaded);
      setFigureName(fig.name);
      setSelectedIds([]);
    }
  };

  const handleDeleteFigure = async (id) => {
    if (!window.confirm('Delete this figure?')) return;
    try { await api.deleteFigure(id); setFigures(figures.filter(f => f.id !== id)); toast.success('Deleted'); }
    catch (e) { toast.error('Failed'); }
  };

  const handleExport = (format, opts) => {
    if (format === 'svg') exportAsSVG(objects, figureName);
    else if (format === 'png') exportAsPNG(objects, figureName, opts.dpi);
    else if (format === 'pdf') exportAsPDF(objects, figureName, opts.preset);
    toast.success(`Exported as ${format.toUpperCase()}`);
  };

  // ==================== AI ====================
  const handleAIAssist = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const result = await api.aiAssist(aiPrompt, 'figure', { object_count: objects.length, figure_name: figureName });
      setAiSuggestion(result.response);
    } catch (e) { toast.error('AI assist failed'); }
    finally { setAiLoading(false); }
  };

  // ==================== BOOLEAN OPERATIONS ====================
  const handleBoolean = useCallback((operation) => {
    if (selectedIds.length !== 2) return;
    const objA = objects.find(o => o.id === selectedIds[0]);
    const objB = objects.find(o => o.id === selectedIds[1]);
    if (!objA || !objB) return;
    const style = { fill: objA.fill, stroke: objA.stroke, strokeWidth: objA.strokeWidth };
    let result = null;
    switch (operation) {
      case 'unite': result = booleanUnite(objA, objB, style); break;
      case 'subtract': result = booleanSubtract(objA, objB, style); break;
      case 'intersect': result = booleanIntersect(objA, objB, style); break;
      case 'exclude': result = booleanExclude(objA, objB, style); break;
      default: return;
    }
    if (result) {
      const newObjects = objects.filter(o => !selectedIds.includes(o.id)).concat(result);
      setObjects(newObjects);
      pushHistory(newObjects);
      setSelectedIds([result.id]);
      toast.success(`Boolean ${operation} applied`);
    } else {
      toast.error('Boolean operation failed - shapes may not overlap');
    }
  }, [selectedIds, objects, pushHistory]);

  // ==================== GROUP / UNGROUP ====================
  const handleGroup = useCallback(() => {
    if (selectedIds.length < 2) return;
    const children = objects.filter(o => selectedIds.includes(o.id));
    const bbox = children.reduce((acc, c) => {
      const b = getBBox(c);
      return {
        x: Math.min(acc.x, b.x), y: Math.min(acc.y, b.y),
        x2: Math.max(acc.x2, b.x + b.w), y2: Math.max(acc.y2, b.y + b.h),
      };
    }, { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity });
    const group = {
      id: genId(), type: 'group', x: bbox.x, y: bbox.y,
      w: bbox.x2 - bbox.x, h: bbox.y2 - bbox.y,
      children: JSON.parse(JSON.stringify(children)),
      visible: true, locked: false, opacity: 1, label: 'Group',
    };
    const newObjects = objects.filter(o => !selectedIds.includes(o.id)).concat(group);
    setObjects(newObjects);
    pushHistory(newObjects);
    setSelectedIds([group.id]);
  }, [selectedIds, objects, pushHistory]);

  const handleUngroup = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const obj = objects.find(o => o.id === selectedIds[0]);
    if (!obj || obj.type !== 'group') return;
    const children = (obj.children || []).map(c => ({ ...c, id: genId() }));
    const newObjects = objects.filter(o => o.id !== obj.id).concat(children);
    setObjects(newObjects);
    pushHistory(newObjects);
    setSelectedIds(children.map(c => c.id));
  }, [selectedIds, objects, pushHistory]);

  // ==================== GRADIENT / PATTERN / EFFECTS ====================
  const handleApplyGradient = useCallback((type) => {
    if (!selectedIds.length) { toast.error('Select an object first'); return; }
    const id = selectedIds[0];
    const grad = type === 'radial' ? createRadialGradient() : createLinearGradient();
    const newObjects = objects.map(o => o.id === id ? { ...o, gradient: grad } : o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [selectedIds, objects, pushHistory]);

  const handleApplyPattern = useCallback((patternType) => {
    if (!selectedIds.length) { toast.error('Select an object first'); return; }
    const id = selectedIds[0];
    const pat = createPattern(null, 20, 20, patternType);
    const newObjects = objects.map(o => o.id === id ? { ...o, pattern: pat } : o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [selectedIds, objects, pushHistory]);

  const handleApplyEffect = useCallback((effects) => {
    if (!selectedIds.length) { toast.error('Select an object first'); return; }
    const id = selectedIds[0];
    const obj = objects.find(o => o.id === id);
    const existing = obj?.effects || [];
    const newEffects = [...existing, ...effects];
    const newObjects = objects.map(o => o.id === id ? { ...o, effects: newEffects } : o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [selectedIds, objects, pushHistory]);

  const handleRemoveEffect = useCallback((index) => {
    if (!selectedIds.length) return;
    const id = selectedIds[0];
    const obj = objects.find(o => o.id === id);
    if (!obj?.effects) return;
    let newEffects;
    if (index === 'all') newEffects = [];
    else newEffects = obj.effects.filter((_, i) => i !== index);
    const newObjects = objects.map(o => o.id === id ? { ...o, effects: newEffects } : o);
    setObjects(newObjects);
    pushHistory(newObjects);
  }, [selectedIds, objects, pushHistory]);

  // ==================== SYMBOLS ====================
  const handleSaveSymbol = useCallback(() => {
    if (!selectedIds.length) return;
    const selected = objects.filter(o => selectedIds.includes(o.id));
    const name = prompt('Symbol name:', `Symbol ${symbols.length + 1}`);
    if (!name) return;
    const sym = createSymbol(name, selected);
    setSymbols(prev => [...prev, sym]);
    toast.success(`Saved symbol "${name}"`);
  }, [selectedIds, objects, symbols]);

  const handleInstantiateSymbol = useCallback((sym) => {
    const newObjs = instantiateSymbol(sym, 50, 50);
    const all = [...objects, ...newObjs];
    setObjects(all);
    pushHistory(all);
    setSelectedIds(newObjs.map(o => o.id));
  }, [objects, pushHistory]);

  // ==================== DIRECT SELECT (anchor manipulation) ====================
  useEffect(() => {
    if (tool === TOOLS.DIRECT_SELECT && selectedIds.length === 1) {
      const obj = objects.find(o => o.id === selectedIds[0]);
      if (obj?.type === 'path' && obj.d) {
        setDirectSelectPoints(parseSVGPath(obj.d));
      } else {
        setDirectSelectPoints([]);
      }
    } else {
      setDirectSelectPoints([]);
      setDirectSelectIdx(-1);
    }
  }, [tool, selectedIds, objects]);

  // ==================== ANCHOR POINT TOOL ====================
  // When anchor point tool is active and an object is selected, convert it to anchor points
  useEffect(() => {
    if (tool === TOOLS.ANCHOR_POINT && selectedIds.length === 1) {
      const obj = objects.find(o => o.id === selectedIds[0]);
      if (obj) {
        const pts = shapeToAnchorPoints(obj);
        if (pts.length > 0) {
          setAnchorPoints(pts);
          setAnchorObjId(obj.id);
          return;
        }
      }
      setAnchorPoints([]);
      setAnchorObjId(null);
    } else if (tool === TOOLS.ANCHOR_POINT && selectedIds.length === 0) {
      // Keep existing anchor points if we just deselected from clicking canvas
      // (user might click an anchor point next)
    } else if (tool !== TOOLS.ANCHOR_POINT) {
      // Commit anchor changes when switching away from the tool
      if (anchorPoints.length > 0 && anchorObjId) {
        const obj = objects.find(o => o.id === anchorObjId);
        if (obj) {
          const isClosed = obj.type !== 'line';
          const d = anchorPointsToPath(anchorPoints, isClosed);
          const xs = anchorPoints.map(p => p.x);
          const ys = anchorPoints.map(p => p.y);
          const minX = Math.min(...xs), minY = Math.min(...ys);
          const maxX = Math.max(...xs), maxY = Math.max(...ys);
          updateObject(anchorObjId, {
            type: 'path', d,
            x: minX, y: minY, w: maxX - minX, h: maxY - minY,
          });
          pushHistory([...objects]);
        }
      }
      setAnchorPoints([]);
      setAnchorObjId(null);
      setAnchorSelectedIdx(-1);
      setAnchorDragging(null);
    }
  }, [tool, selectedIds]);

  const handleAnchorClick = useCallback((index) => {
    if (tool !== TOOLS.ANCHOR_POINT) return;
    // Toggle smooth/corner on click
    const newPoints = toggleAnchorPointSmooth(anchorPoints, index);
    setAnchorPoints(newPoints);
    setAnchorSelectedIdx(index);
    // Immediately update the object preview
    if (anchorObjId) {
      const obj = objects.find(o => o.id === anchorObjId);
      const isClosed = obj?.type !== 'line';
      const d = anchorPointsToPath(newPoints, isClosed);
      updateObject(anchorObjId, { type: 'path', d });
    }
  }, [tool, anchorPoints, anchorObjId, objects, updateObject]);

  const handleAnchorHandleDrag = useCallback((index, handleType, absX, absY) => {
    if (tool !== TOOLS.ANCHOR_POINT) return;
    const newPoints = dragAnchorHandle(anchorPoints, index, handleType, absX, absY);
    setAnchorPoints(newPoints);
    // Immediately update the object preview
    if (anchorObjId) {
      const obj = objects.find(o => o.id === anchorObjId);
      const isClosed = obj?.type !== 'line';
      const d = anchorPointsToPath(newPoints, isClosed);
      updateObject(anchorObjId, { type: 'path', d });
    }
  }, [tool, anchorPoints, anchorObjId, objects, updateObject]);

  // Drag an anchor point itself to pull out bezier handles (Illustrator-style)
  const handleAnchorPointDrag = useCallback((index, absX, absY) => {
    if (tool !== TOOLS.ANCHOR_POINT) return;
    const pt = anchorPoints[index];
    if (!pt) return;
    // The drag distance from the anchor point determines the handle length/direction
    const dx = absX - pt.x;
    const dy = absY - pt.y;
    const newPoints = [...anchorPoints];
    newPoints[index] = {
      ...pt,
      cx1: pt.x - dx,
      cy1: pt.y - dy,
      cx2: pt.x + dx,
      cy2: pt.y + dy,
    };
    setAnchorPoints(newPoints);
    setAnchorSelectedIdx(index);
    // Immediately update the object preview
    if (anchorObjId) {
      const obj = objects.find(o => o.id === anchorObjId);
      const isClosed = obj?.type !== 'line';
      const d = anchorPointsToPath(newPoints, isClosed);
      updateObject(anchorObjId, { type: 'path', d });
    }
  }, [tool, anchorPoints, anchorObjId, objects, updateObject]);

  const selectedObj = objects.find(o => selectedIds.length === 1 && o.id === selectedIds[0]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="figure-maker-page" className="flex flex-col" style={{ height: 'calc(100vh - 100px)' }}>
      {/* Toolbar */}
      <EditorToolbar
        tool={tool} setTool={setTool}
        zoom={zoom} setZoom={setZoom}
        gridEnabled={gridEnabled} setGridEnabled={setGridEnabled}
        snapEnabled={snapEnabled} setSnapEnabled={setSnapEnabled}
        showRulers={showRulers} setShowRulers={setShowRulers}
        undo={undo} redo={redo}
        canUndo={historyIdx > 0} canRedo={historyIdx < history.length - 1}
        selectedIds={selectedIds} objects={objects}
        onAlign={handleAlign} onDistribute={handleDistribute}
        onGroup={handleGroup} onUngroup={handleUngroup}
        onDelete={deleteSelected} onDuplicate={duplicateSelected}
        onBoolean={handleBoolean}
        figureName={figureName} setFigureName={setFigureName}
        onSave={saveFigure}
        onExport={() => setShowExport(true)}
        onAIToggle={() => setShowAI(!showAI)}
        showAI={showAI}
      />

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel */}
        <EditorLeftPanel
          currentStyle={currentStyle} onStyleChange={handleStyleChange}
          selectedObj={selectedObj} onUpdateObject={updateObjectAndCommit}
          onApplyGradient={handleApplyGradient}
          onApplyPattern={handleApplyPattern}
          onApplyEffect={handleApplyEffect}
          onRemoveEffect={handleRemoveEffect}
          symbols={symbols}
          onSaveSymbol={handleSaveSymbol}
          onInstantiateSymbol={handleInstantiateSymbol}
        />

        {/* Canvas */}
        <div ref={containerRef} className="flex-1 overflow-hidden bg-slate-100 relative"
          onContextMenu={(e) => e.preventDefault()}>
          <EditorCanvas
            objects={objects} selectedIds={selectedIds}
            tool={tool} zoom={zoom} panX={panX} panY={panY}
            gridEnabled={gridEnabled} snapEnabled={snapEnabled} showRulers={showRulers}
            penPoints={penPoints} currentStyle={currentStyle}
            snapGuides={snapGuides} rubberBand={rubberBand}
            lassoPoints={lassoPoints}
            clipMasks={clipMasks}
            directSelectPoints={directSelectPoints}
            directSelectIdx={directSelectIdx}
            anchorPoints={anchorPoints}
            anchorSelectedIdx={anchorSelectedIdx}
            onAnchorClick={handleAnchorClick}
            onAnchorHandleDrag={handleAnchorHandleDrag}
            onAnchorPointDrag={handleAnchorPointDrag}
            onMouseDown={handleResizeStart}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onCanvasClick={handleCanvasClick}
            onObjectMouseDown={handleObjectMouseDown}
            onWheel={handleWheel}
            canvasW={canvasSize.w} canvasH={canvasSize.h}
          />
        </div>

        {/* Right Panel */}
        <EditorRightPanel
          objects={objects} selectedIds={selectedIds} selectedObj={selectedObj}
          onUpdateObject={updateObjectAndCommit} onSelectObject={(id) => setSelectedIds([id])}
          onMoveLayer={moveLayer} onToggleVisibility={toggleVisibility} onToggleLock={toggleLock}
          onDeleteObject={(id) => { const newObjs = objects.filter(o => o.id !== id); setObjects(newObjs); pushHistory(newObjs); setSelectedIds(s => s.filter(i => i !== id)); }}
          onGroup={handleGroup} onUngroup={handleUngroup}
          figures={figures} onLoadFigure={loadSavedFigure} onDeleteFigure={handleDeleteFigure}
          onLoadTemplate={loadTemplate}
          showAI={showAI} aiPrompt={aiPrompt} setAiPrompt={setAiPrompt}
          aiSuggestion={aiSuggestion} aiLoading={aiLoading} onAIAssist={handleAIAssist}
        />
      </div>

      {/* Export Dialog */}
      {showExport && <ExportDialog onClose={() => setShowExport(false)} onExport={handleExport} />}
    </div>
  );
};

export default FigureMakerPage;
